/**
 * Gezinssamenstelling (family composition) reuse helper.
 *
 * The user only picks a family composition once; every other selected product reuses it. Each
 * product stores it under its own AFD field code (OP_GSS, IP_GSS, DA_GSS, VK_GEZSAMST, WC_GSS,
 * RP_GSS, OD_GSS, ...) and sometimes in a different Contractdocument section, but the option codes
 * (A/H/I/K) are identical across products. So instead of hard-coding one path per product, scan the
 * product's stored data for any gezinssamenstelling field with a valid value — robust to which
 * label/section actually holds it after a recalc reshapes the document.
 */

import { SaaProductId } from './addressSharing';
import { getProductOrder } from '../workflowConfig';

/** The gezinssamenstelling option codes, identical across products. */
export const FAMILY_COMPOSITION_VALUES = ['A', 'H', 'I', 'K'] as const;

/** GSS/GSSC code -> i18n key for PP_CONTROM (summary, confirmation, enrich, AVP). */
export const GSS_CODE_TO_I18N_KEY: Record<string, string> = {
  A: 'screens.aansprakelijkheid.options.familyComposition.singleWithoutChildren',
  H: 'screens.aansprakelijkheid.options.familyComposition.coupleWithoutChildren',
  I: 'screens.aansprakelijkheid.options.familyComposition.coupleWithChildren',
  K: 'screens.aansprakelijkheid.options.familyComposition.singleWithChildren',
};

const GSS_VALUES = new Set<string>(FAMILY_COMPOSITION_VALUES);
const isGssKey = (key: string): boolean => /(_GSS|GEZSAMST)$/i.test(key) || key.toUpperCase() === 'GSS';

const scanForGss = (node: any, depth = 0): string | null => {
  if (!node || typeof node !== 'object' || depth > 6) return null;
  for (const [key, value] of Object.entries(node)) {
    if (typeof value === 'string' && isGssKey(key) && GSS_VALUES.has(value)) return value;
  }
  for (const value of Object.values(node)) {
    if (value && typeof value === 'object') {
      const found = scanForGss(value, depth + 1);
      if (found) return found;
    }
  }
  return null;
};

export const readFamilyComposition = (productData: any): string | null => scanForGss(productData);

export const findUpstreamFamilyComposition = (
  getStepData: (id: string) => any,
  currentProductId: SaaProductId,
  selectedProducts?: Record<string, boolean | undefined>,
): string | null => {
  for (const id of getProductOrder()) {
    if (id === currentProductId) break;
    if (selectedProducts && !selectedProducts[id]) continue;
    const found = scanForGss(getStepData(id));
    if (found) return found;
  }
  return null;
};

export const findSharedFamilyComposition = (
  getStepData: (id: string) => any,
): string | null => {
  // Scan EVERY configured product, selected or not. Deselecting a product does not clear its step
  // data, so a value the user typed on a product they later deselected must still carry over to a
  // newly-selected one (fill Rechtsbijstand -> deselect it -> select Aansprakelijkheid -> reuse it).
  // Ownership/locking is handled separately by findUpstream* (which does filter by selection).
  for (const id of getProductOrder()) {
    const found = scanForGss(getStepData(id));
    if (found) return found;
  }
  return null;
};

const GSS_FIELD_PATH: Partial<Record<SaaProductId, [section: string, code: string]>> = {
  woonhuis: ['OP', 'OP_GSS'],
  inboedel: ['IP', 'IP_GSS'],
  aansprakelijkheid: ['DA', 'DA_GSS'],
  rechtsbijstand: ['VK', 'VK_GEZSAMST'],
};

export const syncFamilyCompositionToOtherProducts = (
  sourceProductId: SaaProductId,
  gss: string,
  selectedProducts: Record<string, boolean | undefined> | undefined,
  getStepData: (id: string) => any,
  saveStepData: (id: string, data: any) => void,
): void => {
  if (!selectedProducts || !GSS_VALUES.has(gss)) return;

  for (const product of getProductOrder()) {
    if (product === sourceProductId || !selectedProducts[product]) continue;
    const stepData = getStepData(product);
    if (!stepData) continue;

    if (product === 'ongevallen' || product === 'doorlopendereis') {
      if (stepData.values?.OD_GSS === gss) continue;
      saveStepData(product, { ...stepData, values: { ...(stepData.values || {}), OD_GSS: gss } });
      continue;
    }

    const spec = GSS_FIELD_PATH[product];
    const pp = stepData?.Contractdocument?.PP;
    if (!spec || !pp) continue;
    const [section, code] = spec;
    if (pp[section]?.[code] === gss) continue;
    saveStepData(product, {
      ...stepData,
      Contractdocument: {
        ...stepData.Contractdocument,
        PP: { ...pp, [section]: { ...(pp[section] || {}), [code]: gss } },
      },
    });
  }
};
